import React, { useState, useEffect, useMemo } from 'react';import axios from 'axios';
import { useClient } from '../context/ClientContext';
import { Sparkles, Download, Loader2, TrendingUp, TrendingDown, Minus, FileText, FileSpreadsheet, Search, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { ModuleUploadZone } from '../components/ModuleUploadZone';
import { useFilterState } from '../hooks/useFilterState';

const monthLabel = (y, m) => new Date(y, m - 1, 1).toLocaleString('en-IN', { month: 'long', year: 'numeric' });

export const FinancialAnalysis = () => {
  const { selectedClient, API } = useClient();
  const now = new Date();
  // Default to previous month (where data is usually complete)
  const initialMonth = now.getMonth() === 0 ? 12 : now.getMonth();
  const initialYear = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();

  // Persist year+month per client so nav between tabs doesn't reset the period
  const [period, setPeriod] = useFilterState('financial-analysis', selectedClient?.id, {
    year: initialYear, month: initialMonth,
  });
  const year = period.year;
  const month = period.month;
  const setYear = (y) => setPeriod({ ...period, year: y });
  const setMonth = (m) => setPeriod({ ...period, month: m });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState('');
  const [summaryLoading, setSummaryLoading] = useState(false);

  const load = async () => {
    if (!selectedClient) return;
    setLoading(true);
    setSummary('');
    try {
      const res = await axios.get(`${API}/financial-analysis/${selectedClient.id}`, {
        params: { year, month, auto_fallback: true }
      });
      setData(res.data);
    } catch (e) {
      console.error(e); setData(null);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [selectedClient, year, month]);

  // Use the effective period (post-fallback) so summary + downloads match what's on-screen.
  const effectiveYear = data?.effective_period?.year ?? year;
  const effectiveMonth = data?.effective_period?.month ?? month;

  const loadSummary = async () => {
    setSummaryLoading(true);
    try {
      const res = await axios.get(`${API}/financial-analysis/${selectedClient.id}/executive-summary`,
        { params: { year: effectiveYear, month: effectiveMonth } });
      setSummary(res.data.executive_summary || 'No summary generated.');
    } catch (e) {
      setSummary('Failed to generate summary.');
    } finally { setSummaryLoading(false); }
  };

  const downloadReport = async (reportType) => {
    try {
      const res = await axios.get(
        `${API}/financial-analysis/${selectedClient.id}/report`,
        { params: { year: effectiveYear, month: effectiveMonth, report_type: reportType }, responseType: 'blob' }
      );
      const blob = new Blob([res.data], {
        type: reportType === 'excel'
          ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          : 'application/pdf'
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ext = reportType === 'excel' ? 'xlsx' : 'pdf';
      a.download = `financial_analysis_${selectedClient.id}_${effectiveYear}_${String(effectiveMonth).padStart(2, '0')}_${reportType}.${ext}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert('Download failed: ' + (e?.response?.data?.detail || e.message));
    }
  };

  const yearOptions = useMemo(() => {
    const arr = [];
    for (let y = now.getFullYear(); y >= 2022; y--) arr.push(y);
    return arr;
  }, [now]);

  if (!selectedClient) return <div className="p-8 text-text-muted">Select a client to view financial analysis.</div>;

  return (
    <div className="space-y-6" data-testid="financial-analysis-page">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-primary">Financial Analysis</h1>
          <p className="text-text-secondary text-sm mt-1">
            IFRS-flavoured P&amp;L · MoM &amp; YoY · {selectedClient.name}
          </p>
        </div>
        <div className="flex items-end space-x-2">
          <PeriodSelect label="Month" value={month} onChange={setMonth}
            options={Array.from({length: 12}, (_, i) => ({ value: i+1, label: new Date(2000, i, 1).toLocaleString('en-IN', { month: 'long' }) }))} testid="fa-month-select" />
          <PeriodSelect label="Year" value={year} onChange={setYear}
            options={yearOptions.map(y => ({ value: y, label: y }))} testid="fa-year-select" />
          <div className="flex space-x-2 ml-3">
            <Button onClick={() => downloadReport('brief')} variant="outline" size="sm" data-testid="dl-brief"><FileText className="h-3.5 w-3.5 mr-1" /> Brief PDF</Button>
            <Button onClick={() => downloadReport('detailed')} variant="outline" size="sm" data-testid="dl-detailed"><FileText className="h-3.5 w-3.5 mr-1" /> Detailed PDF</Button>
            <Button onClick={() => downloadReport('excel')} variant="outline" size="sm" data-testid="dl-excel"><FileSpreadsheet className="h-3.5 w-3.5 mr-1" /> Excel</Button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="bg-white border border-border rounded-md p-12 text-center text-text-muted">
          <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" /> Calculating...
        </div>
      ) : !data ? (
        <div className="bg-white border border-border rounded-md p-12 text-center text-text-muted">
          No data for {monthLabel(year, month)}.
        </div>
      ) : (
        <>
          {data.fallback_applied && data.effective_period && (
            <div
              className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 flex items-start gap-2"
              data-testid="fa-fallback-banner"
              role="status"
            >
              <span className="mt-0.5">⚠️</span>
              <div>
                <div className="font-medium">
                  Showing {monthLabel(data.effective_period.year, data.effective_period.month)} — your most recent month with uploaded data.
                </div>
                <div className="text-amber-800">{data.fallback_reason}</div>
              </div>
            </div>
          )}
          {/* Quick AI revenue upload (in case the period has no data) */}
          {data.current.operations.total_revenue === 0 && (
            <div className="bg-blue-50 border border-state-info/30 rounded-md p-4">
              <p className="text-sm font-semibold text-state-info mb-2">No revenue data for {monthLabel(year, month)} yet</p>
              <p className="text-xs text-text-secondary mb-3">Upload a daily revenue / POS sales report below and AI will fill in this analysis automatically.</p>
              <ModuleUploadZone module="revenue" moduleLabel="Daily Revenue / POS Sales Report"
                clientId={selectedClient.id} API={API} onUploadComplete={load} />
            </div>
          )}

          {/* Executive Summary */}
          <div className="bg-white border border-border rounded-md p-5" data-testid="fa-exec-summary">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-primary flex items-center"><Sparkles className="h-4 w-4 mr-2 text-accent" /> CFO Executive Summary</h3>
              <Button onClick={loadSummary} disabled={summaryLoading} size="sm" variant="outline" data-testid="generate-fa-summary">
                {summaryLoading ? <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Generating...</> : (summary ? 'Regenerate' : 'Generate IFRS Commentary')}
              </Button>
            </div>
            {summary ? (
              <p className="text-sm text-text-secondary whitespace-pre-wrap leading-relaxed">{summary}</p>
            ) : (
              <p className="text-sm text-text-muted">Click "Generate" for GPT-5.2's 4-paragraph CFO-level commentary on top-line growth, margins, cost structure, and recommendations.</p>
            )}
          </div>

          {/* KPI Cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <KPI label="Net Revenue" value={inr(data.current.pnl.revenue.net_revenue)} mom={data.comparisons.net_revenue.mom} yoy={data.comparisons.net_revenue.yoy} testid="kpi-net-rev" />
            <KPI label="EBITDA" value={inr(data.current.pnl.ebitda.amount)} subValue={pct(data.current.pnl.ebitda.margin_pct)} mom={data.comparisons.ebitda.mom} testid="kpi-ebitda" />
            <KPI label="EBITDA Margin" value={pct(data.current.pnl.ebitda.margin_pct)} testid="kpi-ebitda-margin" />
            <KPI label="Food Cost %" value={pct(data.current.pnl.cogs.food_cost_pct)} mom={data.comparisons.food_cost_pct.mom} inverse testid="kpi-food-cost" />
            <KPI label="Overhead Rate" value={pct(data.current.pnl.operating_expenses.overhead_rate_pct)} mom={data.comparisons.overhead_rate_pct.mom} inverse testid="kpi-overhead" />
            <KPI label="Avg Bill/Cover" value={inr(data.current.operations.avg_revenue_per_cover)} subValue={`${data.current.operations.covers} covers`} testid="kpi-bill-cover" />
          </div>

          <Tabs defaultValue="pnl" className="space-y-6">
            <TabsList className="bg-white border border-border">
              <TabsTrigger value="pnl" data-testid="tab-fa-pnl">P&amp;L Statement</TabsTrigger>
              <TabsTrigger value="costs" data-testid="tab-fa-costs">Cost Breakdown</TabsTrigger>
              <TabsTrigger value="ratios" data-testid="tab-fa-ratios">Ratios</TabsTrigger>
              <TabsTrigger value="menu" data-testid="tab-fa-menu">Menu Engineering</TabsTrigger>
              <TabsTrigger value="reviews" data-testid="tab-fa-reviews">Customer Sentiment</TabsTrigger>
              <TabsTrigger value="comparison" data-testid="tab-fa-comparison">MoM vs YoY</TabsTrigger>
            </TabsList>

            <TabsContent value="pnl"><PnLStatement pnl={data.current.pnl} /></TabsContent>
            <TabsContent value="costs"><CostBreakdown pnl={data.current.pnl} /></TabsContent>
            <TabsContent value="ratios"><Ratios pnl={data.current.pnl} ops={data.current.operations} /></TabsContent>
            <TabsContent value="menu"><MenuEngineering data={data.current.menu_engineering} /></TabsContent>
            <TabsContent value="reviews"><ReviewsSection data={data.current.customer_reviews} /></TabsContent>
            <TabsContent value="comparison"><ComparisonTable comparisons={data.comparisons} periodLabel={data.current.period.label} prevMonthLabel={data.prev_month.period.label} prevYearLabel={data.prev_year.period.label} clientId={selectedClient.id} API={API} year={year} month={month} /></TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
};

// ============== Sub-components ==============

const inr = (n) => n != null ? `₹${Math.round(n).toLocaleString('en-IN')}` : '—';
const pct = (n) => n != null ? `${n.toFixed(1)}%` : '—';

const PeriodSelect = ({ label, value, options, onChange, testid }) => (
  <label className="block">
    <span className="text-[10px] uppercase tracking-wider text-text-muted font-semibold">{label}</span>
    <select value={value} onChange={e => onChange(parseInt(e.target.value))}
      className="block mt-1 border border-border rounded px-2 py-1 text-sm bg-white" data-testid={testid}>
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </label>
);

const Delta = ({ d, inverse }) => {
  if (!d) return null;
  if (d.percent == null) return <span className="text-text-muted text-xs">—</span>;
  const positive = inverse ? d.absolute < 0 : d.absolute > 0;
  const negative = inverse ? d.absolute > 0 : d.absolute < 0;
  const cls = positive ? 'text-state-success' : negative ? 'text-state-error' : 'text-text-muted';
  const Icon = d.absolute > 0 ? TrendingUp : d.absolute < 0 ? TrendingDown : Minus;
  return <span className={`inline-flex items-center text-xs font-semibold ${cls}`}><Icon className="h-3 w-3 mr-0.5" />{Math.abs(d.percent).toFixed(1)}%</span>;
};

const KPI = ({ label, value, subValue, mom, yoy, inverse, testid }) => (
  <div className="bg-white border border-border rounded-md p-3" data-testid={testid}>
    <p className="text-[10px] uppercase tracking-wider text-text-muted font-semibold">{label}</p>
    <p className="text-xl font-bold text-primary">{value}</p>
    {subValue && <p className="text-[10px] text-text-secondary">{subValue}</p>}
    <div className="flex space-x-2 mt-1 text-[10px]">
      {mom && <span>MoM <Delta d={mom} inverse={inverse} /></span>}
      {yoy && <span>YoY <Delta d={yoy} inverse={inverse} /></span>}
    </div>
  </div>
);

const PnLStatement = ({ pnl }) => (
  <div className="bg-white border border-border rounded-md overflow-hidden" data-testid="pnl-table">
    <table className="w-full">
      <tbody className="divide-y divide-border">
        <Row label="Gross Revenue" value={inr(pnl.revenue.gross_revenue)} bold />
        <Row label="    Food Revenue" value={inr(pnl.revenue.food_revenue)} muted />
        <Row label="    Beverage Revenue" value={inr(pnl.revenue.beverage_revenue)} muted />
        <Row label="Less: Taxes" value={`- ${inr(pnl.revenue.taxes)}`} neg />
        <Row label="Net Revenue" value={inr(pnl.revenue.net_revenue)} bold />
        <Row label="Less: COGS (Food Cost)" value={`- ${inr(pnl.cogs.food_cost)}`} neg sub={pct(pnl.cogs.food_cost_pct)} />
        <Row label="Gross Profit" value={inr(pnl.gross_profit.amount)} bold pos sub={pct(pnl.gross_profit.margin_pct)} />
        <Row label="Less: Labour" value={`- ${inr(pnl.operating_expenses.labour)}`} neg sub={pct(pnl.operating_expenses.labour_cost_pct)} />
        <Row label="Less: Non-labour Opex" value={`- ${inr(pnl.operating_expenses.non_labour_opex)}`} neg />
        <Row label="EBITDA" value={inr(pnl.ebitda.amount)} bold pos sub={pct(pnl.ebitda.margin_pct)} />
        <tr><td colSpan={3} className="px-4 pt-2 pb-3 text-[11px] italic text-text-muted">
          P&amp;L ends at EBITDA — depreciation, interest and income tax sit with your CA. GST is a pass-through (remitted, not an expense).
        </td></tr>
      </tbody>
    </table>
  </div>
);

const Row = ({ label, value, sub, bold, pos, neg, muted }) => (
  <tr className={muted ? 'opacity-60' : ''}>
    <td className={`px-4 py-2 text-sm ${bold ? 'font-bold' : ''}`}>{label}</td>
    <td className={`px-4 py-2 text-sm text-right tabular-nums ${bold ? 'font-bold' : ''} ${pos ? 'text-state-success' : neg ? 'text-state-error' : ''}`}>{value}</td>
    <td className="px-4 py-2 text-xs text-right text-text-muted w-24">{sub || ''}</td>
  </tr>
);

const CostBreakdown = ({ pnl }) => {
  const cats = pnl.operating_expenses.by_category;
  return (
    <div className="bg-white border border-border rounded-md overflow-hidden" data-testid="cost-breakdown-table">
      <div className="px-5 py-3 border-b border-border bg-background-secondary">
        <h3 className="font-semibold text-primary">All Cost Categories</h3>
        <p className="text-xs text-text-muted">Total Opex: <strong>{inr(pnl.operating_expenses.total_opex)}</strong> · Overhead Rate: <strong>{pct(pnl.operating_expenses.overhead_rate_pct)}</strong></p>
      </div>
      {cats.length === 0 ? <div className="p-8 text-center text-text-muted text-sm">No expenses recorded for this period.</div> :
        <table className="w-full">
          <thead className="bg-primary text-white">
            <tr><th className="px-4 py-2 text-left text-xs uppercase">Category</th><th className="px-4 py-2 text-right text-xs uppercase">Amount</th><th className="px-4 py-2 text-right text-xs uppercase">% of Total</th><th className="px-4 py-2 text-left text-xs uppercase">Share</th></tr>
          </thead>
          <tbody className="divide-y divide-border">
            {cats.map(c => (
              <tr key={c.category} data-testid={`cost-row-${c.category}`}>
                <td className="px-4 py-2 text-sm">{c.category}</td>
                <td className="px-4 py-2 text-sm text-right tabular-nums font-semibold">{inr(c.amount)}</td>
                <td className="px-4 py-2 text-sm text-right tabular-nums">{pct(c.percent_of_total)}</td>
                <td className="px-4 py-2 w-1/3">
                  <div className="h-2 bg-background-secondary rounded overflow-hidden">
                    <div className="h-full bg-primary" style={{width: `${Math.min(100, c.percent_of_total)}%`}} />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      }
    </div>
  );
};

const Ratios = ({ pnl, ops }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="ratios-section">
    <RatioCard title="Profitability" items={[
      ['Gross Margin', pct(pnl.gross_profit.margin_pct)],
      ['EBITDA Margin', pct(pnl.ebitda.margin_pct)],
      ['Contribution Margin', pct(pnl.ratios.contribution_margin_pct)],
    ]} />
    <RatioCard title="Cost Structure" items={[
      ['Food Cost %', pct(pnl.cogs.food_cost_pct)],
      ['Labour Cost %', pct(pnl.operating_expenses.labour_cost_pct)],
      ['Prime Cost', `${inr(pnl.ratios.prime_cost)} (${pct(pnl.ratios.prime_cost_ratio_pct)})`],
      ['Overhead Rate', pct(pnl.operating_expenses.overhead_rate_pct)],
      ['Break-even Revenue', inr(pnl.ratios.break_even_revenue)],
    ]} />
    <RatioCard title="Per Cover / Per Day" items={[
      ['Days in Period', ops.days],
      ['Covers', ops.covers.toLocaleString()],
      ['Avg Revenue / Day', inr(ops.avg_revenue_per_day)],
      ['Avg Covers / Day', ops.avg_covers_per_day],
      ['Avg Revenue / Cover', inr(ops.avg_revenue_per_cover)],
      ['Avg Food Cost / Cover', inr(ops.avg_food_cost_per_cover)],
    ]} />
    <RatioCard title="Revenue Mix" items={[
      ['Food Revenue', inr(pnl.revenue.food_revenue)],
      ['Beverage Revenue', inr(pnl.revenue.beverage_revenue)],
      ['Taxes', inr(pnl.revenue.taxes)],
      ['Supplier Purchases (period)', inr(pnl.cogs.supplier_purchases)],
    ]} />
  </div>
);

const RatioCard = ({ title, items }) => (
  <div className="bg-white border border-border rounded-md p-4">
    <h4 className="font-semibold text-primary text-sm mb-3 uppercase tracking-wider">{title}</h4>
    <table className="w-full">
      <tbody>
        {items.map(([k, v]) => (
          <tr key={k} className="border-b border-border last:border-0">
            <td className="py-1.5 text-sm text-text-secondary">{k}</td>
            <td className="py-1.5 text-sm text-right font-semibold tabular-nums">{v}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const MenuEngineering = ({ data }) => (
  <div className="space-y-4" data-testid="menu-eng-section">
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {[['Star', 'bg-green-100 text-state-success', 'High margin, high sales'],
        ['Plowhorse', 'bg-blue-100 text-state-info', 'Low margin, high sales'],
        ['Puzzle', 'bg-yellow-100 text-state-warning', 'High margin, low sales'],
        ['Dog', 'bg-red-100 text-state-error', 'Low margin, low sales']].map(([label, cls, hint]) => (
          <div key={label} className={`p-4 rounded-md ${cls}`} data-testid={`menu-bucket-${label}`}>
            <p className="text-xs uppercase font-semibold tracking-wider">{label}s</p>
            <p className="text-3xl font-bold">{data.counts[label] || 0}</p>
            <p className="text-[10px] mt-1 opacity-80">{hint}</p>
          </div>
        ))}
    </div>
    {['Star', 'Plowhorse', 'Puzzle', 'Dog'].map(bucket => {
      const items = data.top_per_bucket[bucket] || [];
      if (items.length === 0) return null;
      return (
        <div key={bucket} className="bg-white border border-border rounded-md">
          <div className="px-4 py-2 bg-background-secondary border-b border-border font-semibold text-primary">{bucket}s — Top {items.length}</div>
          <table className="w-full">
            <thead><tr><th className="px-4 py-2 text-left text-xs text-text-muted uppercase">Item</th><th className="px-4 py-2 text-right text-xs text-text-muted uppercase">Revenue</th><th className="px-4 py-2 text-right text-xs text-text-muted uppercase">Margin %</th><th className="px-4 py-2 text-right text-xs text-text-muted uppercase">Qty</th></tr></thead>
            <tbody className="divide-y divide-border">
              {items.map(it => (
                <tr key={it.name}><td className="px-4 py-2 text-sm">{it.name}</td><td className="px-4 py-2 text-sm text-right tabular-nums">{inr(it.total_revenue)}</td><td className="px-4 py-2 text-sm text-right tabular-nums">{pct(it.margin_percentage)}</td><td className="px-4 py-2 text-sm text-right tabular-nums">{it.quantity_sold}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    })}
  </div>
);

const ReviewsSection = ({ data }) => {
  if (!data.count) return <div className="bg-white border border-border rounded-md p-8 text-center text-text-muted text-sm">No reviews for this period. Upload from <strong>Reviews</strong> tab.</div>;
  return (
    <div className="space-y-4" data-testid="fa-reviews-section">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat2 label="Total Reviews" value={data.count} />
        <Stat2 label="Avg Rating" value={`${data.avg_rating} / 5`} />
        <Stat2 label="NPS-ish Score" value={data.nps_score} color={data.nps_score > 0 ? 'text-state-success' : 'text-state-error'} />
        <Stat2 label="Sentiment" value={`${data.by_sentiment.positive || 0}+ / ${data.by_sentiment.negative || 0}-`} />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border border-border rounded-md p-4">
          <h4 className="font-semibold text-primary text-sm mb-2 uppercase tracking-wider">Top Themes</h4>
          {data.top_themes.map(t => (
            <div key={t.theme} className="flex justify-between py-1 text-sm border-b border-border last:border-0"><span className="capitalize">{t.theme}</span><span className="font-semibold">{t.count}</span></div>
          ))}
        </div>
        <div className="bg-white border border-border rounded-md p-4">
          <h4 className="font-semibold text-primary text-sm mb-2 uppercase tracking-wider">Recent Reviews</h4>
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {data.recent.map((r, i) => (
              <div key={i} className="text-xs border-b border-border pb-2">
                <div className="flex justify-between">
                  <strong>{r.reviewer}</strong>
                  <span className="text-text-muted">{r.date} · {r.source}</span>
                </div>
                <p className="text-text-secondary mt-1">{r.text}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const Stat2 = ({ label, value, color = 'text-primary' }) => (
  <div className="bg-white border border-border rounded-md p-3">
    <p className="text-[10px] uppercase tracking-wider text-text-muted font-semibold">{label}</p>
    <p className={`text-xl font-bold ${color}`}>{value}</p>
  </div>
);

const ComparisonTable = ({ comparisons, periodLabel, prevMonthLabel, prevYearLabel, clientId, API, year, month }) => {
  const [investigating, setInvestigating] = useState(null); // {metric, comparison}
  const [explanation, setExplanation] = useState(null);
  const [loading, setLoading] = useState(false);

  const investigate = async (metric, comparison) => {
    setInvestigating({ metric, comparison });
    setLoading(true);
    setExplanation(null);
    try {
      const res = await axios.post(`${API}/financial-analysis/${clientId}/explain-variance`,
        { metric, year, month, comparison });
      setExplanation(res.data);
    } catch (e) {
      setExplanation({ explanation: 'Failed: ' + (e?.response?.data?.detail || e.message) });
    } finally { setLoading(false); }
  };

  const SIGNIFICANT_PCT = 15;
  const SIGNIFICANT_PCT_YOY = 20;

  return (
    <>
      <div className="bg-white border border-border rounded-md overflow-hidden" data-testid="comparison-table">
        <table className="w-full">
          <thead className="bg-primary text-white">
            <tr>
              <th className="px-4 py-2 text-left text-xs uppercase">Metric</th>
              <th className="px-4 py-2 text-right text-xs uppercase">{periodLabel}</th>
              <th className="px-4 py-2 text-right text-xs uppercase">{prevMonthLabel}</th>
              <th className="px-4 py-2 text-right text-xs uppercase">MoM</th>
              <th className="px-4 py-2 text-right text-xs uppercase">{prevYearLabel}</th>
              <th className="px-4 py-2 text-right text-xs uppercase">YoY</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {[
              ['Net Revenue', 'net_revenue', false, 'money'],
              ['Food Cost', 'food_cost', true, 'money'],
              ['Gross Profit', 'gross_profit', false, 'money'],
              ['Total Opex', 'total_opex', true, 'money'],
              ['EBITDA', 'ebitda', false, 'money'],
              ['Food Cost %', 'food_cost_pct', true, 'pct'],
              ['Overhead Rate %', 'overhead_rate_pct', true, 'pct'],
              ['Gross Margin %', 'gross_margin_pct', false, 'pct'],
              ['EBITDA Margin %', 'ebitda_margin_pct', false, 'pct'],
            ].map(([label, key, inverse, type]) => {
              const c = comparisons[key];
              const fmt = (v) => type === 'money' ? inr(v) : pct(v);
              const showMomInvestigate = c.mom.percent != null && Math.abs(c.mom.percent) >= SIGNIFICANT_PCT;
              const showYoyInvestigate = c.yoy.percent != null && Math.abs(c.yoy.percent) >= SIGNIFICANT_PCT_YOY;
              // Auto-flag rows where movement is bad-for-business (favourable swing on inverse, etc.)
              const momBad = c.mom.percent != null && Math.abs(c.mom.percent) >= SIGNIFICANT_PCT &&
                             ((inverse && c.mom.percent > 0) || (!inverse && c.mom.percent < 0));
              const yoyBad = c.yoy.percent != null && Math.abs(c.yoy.percent) >= SIGNIFICANT_PCT_YOY &&
                             ((inverse && c.yoy.percent > 0) || (!inverse && c.yoy.percent < 0));
              const rowFlag = momBad || yoyBad;
              return (
                <tr key={key} data-testid={`comp-row-${key}`} className={rowFlag ? 'bg-red-50' : ''}>
                  <td className="px-4 py-2 text-sm">
                    <div className="flex items-center gap-2">
                      <span>{label}</span>
                      {rowFlag && (
                        <span className="text-[9px] uppercase tracking-wider bg-state-error text-white px-1.5 py-0.5 rounded-sm font-semibold" data-testid={`auto-flag-${key}`}>
                          flag
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-2 text-sm text-right tabular-nums font-semibold">{fmt(c.current)}</td>
                  <td className="px-4 py-2 text-sm text-right tabular-nums text-text-muted">{fmt(c.prev_month)}</td>
                  <td className="px-4 py-2 text-right">
                    <div className="inline-flex items-center space-x-1">
                      <Delta d={c.mom} inverse={inverse} />
                      {showMomInvestigate && (
                        <button onClick={() => investigate(key, 'mom')}
                          className="text-state-info hover:bg-blue-50 rounded p-0.5" title="Investigate variance"
                          data-testid={`investigate-${key}-mom`}>
                          <Search className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-2 text-sm text-right tabular-nums text-text-muted">{fmt(c.prev_year)}</td>
                  <td className="px-4 py-2 text-right">
                    <div className="inline-flex items-center space-x-1">
                      <Delta d={c.yoy} inverse={inverse} />
                      {showYoyInvestigate && (
                        <button onClick={() => investigate(key, 'yoy')}
                          className="text-state-info hover:bg-blue-50 rounded p-0.5" title="Investigate variance"
                          data-testid={`investigate-${key}-yoy`}>
                          <Search className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {investigating && (
        <VarianceModal
          investigating={investigating}
          loading={loading}
          explanation={explanation}
          onClose={() => setInvestigating(null)}
        />
      )}
    </>
  );
};

const VarianceModal = ({ investigating, loading, explanation, onClose }) => {
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-md max-w-2xl w-full p-6 shadow-2xl" onClick={e => e.stopPropagation()} data-testid="variance-modal">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="text-lg font-bold text-primary flex items-center"><Search className="h-4 w-4 mr-2 text-state-info" /> Variance Investigator</h3>
            <p className="text-xs text-text-muted mt-1">{investigating.metric} · {investigating.comparison.toUpperCase()}</p>
          </div>
          <button onClick={onClose} className="text-text-muted hover:text-primary" data-testid="close-variance-modal" aria-label="Close">
            <X className="h-5 w-5" />
          </button>
        </div>
        {loading ? (
          <div className="py-8 text-center text-text-muted text-sm">
            <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
            GPT-5.2 is drilling into the underlying data...
          </div>
        ) : explanation ? (
          <div className="space-y-3">
            <div className="bg-background-secondary rounded p-3 text-xs grid grid-cols-3 gap-3">
              <div><span className="text-text-muted">Current</span><p className="font-bold">{explanation.current_value}</p></div>
              <div><span className="text-text-muted">Base</span><p className="font-bold">{explanation.base_value}</p></div>
              <div><span className="text-text-muted">Change</span><p className="font-bold">{explanation.change?.percent != null ? `${explanation.change.percent}%` : '—'}</p></div>
            </div>
            <p className="text-sm text-text-secondary whitespace-pre-wrap leading-relaxed">{explanation.explanation}</p>
            <p className="text-[10px] text-text-muted">Press <kbd className="bg-background-secondary px-1 py-0.5 rounded">Esc</kbd> or click outside to close.</p>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default FinancialAnalysis;
